/*
 * sk-page-analytics.js — section drop-off, click map, rage and dead clicks.
 *
 * Replaces what Clarity's heatmaps were used for, but as GA4 events, so the
 * data can be queried directly (GA4 Data API / BigQuery export) and split by
 * traffic source - e.g. only visitors from the Shopping ad.
 *
 * Events (all also carry page_template):
 *
 *   section_view   a section scrolled into view (once per section per page)
 *                    section, section_index
 *   scroll_depth   bottom of the screen reached 10/25/50/75/90/100% of the
 *                  page height (each mark once per page)
 *                    percent_scrolled
 *   ui_click       any click/tap
 *                    section, zone, element, x_pct, y_pct
 *   rage_click     3+ clicks within 1s inside a 40px radius (once per burst)
 *                    section, zone, element, clicks
 *   dead_click     a click after which nothing on the page changed for 700ms
 *                    section, zone, element
 *
 *   section  readable section name, e.g. "product", "tryon-preview",
 *            "judgeme-reviews", "collapsible-content". Repeats get a suffix
 *            ("background-video-2").
 *   zone     finer area inside the product section: gallery | builder |
 *            product-info | sticky-buy. Empty elsewhere.
 *   element  what was clicked: a stable class name plus a short label, e.g.
 *            "si-color-btn:Sage Green", "si-size-btn:M", "slider-btn--next".
 *            Never input values - only button/link labels and aria-labels.
 *   x_pct    horizontal position as % of viewport width (0-100)
 *   y_pct    vertical position as % of the section's height (0-100)
 *
 * Transport is Shopify.analytics.publish() only, like the builder, nav and
 * try-on trackers: GA4 runs inside the Web Pixels sandbox, and the custom
 * pixel "Builder analytics" forwards these event names to GA4. If the pixel
 * does not list a name, that event simply does not reach GA4.
 *
 * Cost: one IntersectionObserver, one passive click listener, and a short
 * MutationObserver only while a click is being checked for "dead". No
 * network requests of its own.
 *
 * Every send is wrapped: analytics must never break the page.
 * Debug: add ?si_debug_analytics=1 to the URL to log events to the console.
 */
(function () {
  if (window.SIPageAnalyticsLoaded) return;
  window.SIPageAnalyticsLoaded = true;
  if (!('IntersectionObserver' in window)) return;

  var DEBUG = /[?&]si_debug_analytics=1/.test(window.location.search);

  function send(name, payload) {
    try {
      payload = Object.assign({ page_template: template() }, payload || {});
      if (DEBUG) console.log('[si-page-analytics]', name, payload);
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) {
      if (DEBUG) console.warn('[si-page-analytics] send failed', e);
    }
  }

  function template() {
    return (document.body.className.match(/template-([a-z0-9-]+)/) || [])[1] || '';
  }

  function clip(s, n) {
    s = (s || '').replace(/\s+/g, ' ').trim();
    return s.length > n ? s.slice(0, n) : s;
  }

  // --- section names -------------------------------------------------------
  // Shopify wraps every section in .shopify-section with a class like
  // "section-judgeme-reviews". The main product section uses "product-section"
  // and the try-on slider has no section-* class, so fall back to the id.
  var sectionName = new WeakMap();
  var seenNames = {};

  function nameFor(el, index) {
    var cls = el.className || '';
    var m = cls.match(/\bsection-([a-z0-9-]+)/);
    var name;
    if (/\bproduct-section\b/.test(cls)) name = 'product';
    else if (m && m[1] !== 'spacer') name = m[1];
    else if (m && m[1] === 'spacer') name = 'spacer';
    else {
      var id = (el.id || '').split('__').pop() || ('section-' + index);
      name = id.replace(/_[A-Za-z0-9]{5,}$/, '').replace(/_/g, '-').toLowerCase();
      if (/^[0-9a-f]{16,}$/.test(name)) name = 'section-' + index;
      if (/tryon/.test(name)) name = 'tryon-preview';
    }
    if (el.closest && el.closest('#main-content') === null) {
      if (/footer|baseline/.test(el.id + ' ' + cls)) name = 'footer';
      else if (/header/.test(el.id + ' ' + cls)) name = 'header';
    }
    seenNames[name] = (seenNames[name] || 0) + 1;
    return seenNames[name] > 1 ? name + '-' + seenNames[name] : name;
  }

  var sections = Array.prototype.slice.call(document.querySelectorAll('.shopify-section'));
  sections.forEach(function (el, i) { sectionName.set(el, { name: nameFor(el, i), index: i }); });

  function sectionOf(node) {
    var el = node && node.closest ? node.closest('.shopify-section') : null;
    return el ? (sectionName.get(el) || { name: 'unknown', index: -1 }) : { name: 'none', index: -1 };
  }

  // --- section_view --------------------------------------------------------
  // "Reached" = the section's top has scrolled above 60% of the viewport, so
  // a very tall section still counts without needing 25% of it visible.
  var viewed = {};
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var info = sectionName.get(entry.target);
      if (!info || viewed[info.name] || info.name === 'spacer' || /^spacer-/.test(info.name)) return;
      viewed[info.name] = true;
      send('section_view', { section: info.name, section_index: info.index });
      io.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -40% 0px', threshold: 0 });
  sections.forEach(function (el) { io.observe(el); });

  // --- scroll_depth --------------------------------------------------------
  // Same buckets as Clarity's scroll report. "Reached 50" = the bottom of the
  // screen got to half the page height. Each mark is sent once per page; a
  // jump straight to the bottom sends every mark it passed.
  // GA4's own enhanced-measurement scroll event never fires here (it can't
  // see the page from the pixel sandbox) and only knows 90% anyway.
  var MARKS = [10, 25, 50, 75, 90, 100];
  var reached = 0;
  var scrollQueued = false;
  function checkScroll() {
    scrollQueued = false;
    var doc = document.documentElement;
    var height = Math.max(doc.scrollHeight, document.body.scrollHeight);
    if (height <= window.innerHeight) return;
    var pct = 100 * (window.scrollY + window.innerHeight) / height;
    while (reached < MARKS.length && pct >= MARKS[reached] - 0.5) {
      send('scroll_depth', { percent_scrolled: MARKS[reached] });
      reached++;
    }
    if (reached >= MARKS.length) window.removeEventListener('scroll', onScroll);
  }
  function onScroll() {
    if (scrollQueued) return;
    scrollQueued = true;
    window.requestAnimationFrame(checkScroll);
  }
  window.addEventListener('scroll', onScroll, { passive: true });

  // --- click helpers -------------------------------------------------------
  var INTERACTIVE = 'a,button,input,select,textarea,label,summary,[role="button"],[onclick],[tabindex]';
  var GENERIC = /^(button|full|outline|lazyautosizes|ls-is-cached|is-[a-z-]+|active|selected|aspect-ratio.*|item-single.*|row|columns)$/;

  function zoneOf(node) {
    if (!node.closest) return '';
    if (node.closest('.goods-add-to-cart-sticky, sk-sticky-buy')) return 'sticky-buy';
    if (node.closest('.si-cover-builder, sk-option-picker')) return 'builder';
    if (node.closest('.item-image-container, #Product-Slider, .pswp')) return 'gallery';
    if (node.closest('.product-information')) return 'product-info';
    if (node.closest('.jdgm-modal')) return 'review-viewer';
    if (node.closest('.si-tryon-lightbox')) return 'tryon-viewer';
    return '';
  }

  function elementKey(target) {
    var el = (target.closest && target.closest(INTERACTIVE + ',img')) || target;
    if (el === document.documentElement || el === document.body) return 'page-background';
    if (el.getAttribute && el.getAttribute('data-si-track')) return clip(el.getAttribute('data-si-track'), 90);
    var cls = '';
    var list = (el.getAttribute && el.getAttribute('class') || '').split(/\s+/);
    for (var i = 0; i < list.length; i++) {
      if (list[i] && !GENERIC.test(list[i])) { cls = list[i]; break; }
    }
    var base = cls || (el.id && el.id.length < 30 ? '#' + el.id : el.tagName.toLowerCase());
    // Labels only from controls; never read what a shopper typed.
    var label = '';
    if (!/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) {
      label = el.getAttribute('aria-label') || el.getAttribute('title') || el.getAttribute('data-value') ||
              (/^(BUTTON|A|SUMMARY|LABEL)$/.test(el.tagName) ? el.textContent : '') || '';
    }
    label = clip(label, 30);
    return clip(label ? base + ':' + label : base, 90);
  }

  function position(ev, node) {
    var x = Math.round(100 * ev.clientX / Math.max(1, window.innerWidth));
    var sec = node.closest ? node.closest('.shopify-section') : null;
    var y = -1;
    if (sec) {
      var r = sec.getBoundingClientRect();
      y = Math.round(100 * (ev.clientY - r.top) / Math.max(1, r.height));
    }
    return { x_pct: Math.max(0, Math.min(100, x)), y_pct: Math.max(-1, Math.min(100, y)) };
  }

  // --- rage clicks ---------------------------------------------------------
  var recent = [];
  var rageReportedAt = 0;
  function checkRage(ev, info) {
    var now = Date.now();
    recent.push({ t: now, x: ev.clientX, y: ev.clientY });
    recent = recent.filter(function (c) { return now - c.t < 1000; });
    var near = recent.filter(function (c) {
      return Math.abs(c.x - ev.clientX) < 40 && Math.abs(c.y - ev.clientY) < 40;
    });
    if (near.length >= 3 && now - rageReportedAt > 1500) {
      rageReportedAt = now;
      send('rage_click', Object.assign({ clicks: near.length }, info));
    }
  }

  // --- dead clicks ---------------------------------------------------------
  // Watch for 700ms after a click. A DOM change, scroll or navigation means
  // the click did something. Only one check runs at a time.
  //
  // Scope matters on this store: background videos, logo marquees and
  // autoplay sliders mutate the page constantly, so watching the whole
  // document meant nothing was ever "dead". So: watch only the clicked
  // section plus <body> direct children (where drawers, modals and the
  // review / try-on viewers are appended), and ignore pure `style` changes,
  // which is how those animations move.
  //
  // Autoplay carousels still flip `class` on their slides and dots every few
  // seconds - the testimonial carousel inside the builder
  // (sk-carousel[data-autoplay=5000]) made taps anywhere in the product
  // section look "alive". A change inside a self-moving component only counts
  // when the tap was inside it too. Manual sliders (the gallery) have no
  // data-autoplay, so their slide changes still count as a reaction.
  var AUTO_MOVING = '[data-autoplay]:not([data-autoplay="false"]):not([data-autoplay="0"]), marquee, [class*="marquee"]';
  // Small scroll jitter (mobile address bar, scroll anchoring when lazy
  // images load) is not a reaction to the tap.
  var SCROLL_TOLERANCE = 30;

  function insideAutoMoving(node, control) {
    var auto = node.closest(AUTO_MOVING);
    return !!auto && !auto.contains(control);
  }

  var deadCheck = null;
  function checkDead(info, target) {
    if (deadCheck || !('MutationObserver' in window)) return;
    var reason = '';
    var y0 = window.scrollY;
    var href0 = window.location.href;
    var control = (target.closest && target.closest(INTERACTIVE)) || target;
    var scope = (target.closest && target.closest('.shopify-section')) || document.body;
    var mo = new MutationObserver(function (records) {
      if (reason) return;
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        if (r.type === 'attributes' && r.attributeName === 'style') continue;
        var node = r.target.nodeType === 1 ? r.target : r.target.parentElement;
        if (!node) continue;
        // Overlays, drawers and scroll locks live on <body>/<html>.
        if (node === document.body || node === document.documentElement) {
          reason = r.type + ':' + (r.attributeName || '') + ' on ' + node.tagName;
          return;
        }
        if (insideAutoMoving(node, control)) continue;
        reason = r.type + ':' + (r.attributeName || '') + ' on ' + elementKey(node);
        return;
      }
    });
    mo.observe(scope, { subtree: true, childList: true, attributes: true, characterData: true });
    mo.observe(document.body, { childList: true, attributes: true });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    function onHide() { reason = 'navigated'; }
    window.addEventListener('pagehide', onHide, { once: true });
    deadCheck = setTimeout(function () {
      mo.disconnect();
      window.removeEventListener('pagehide', onHide);
      deadCheck = null;
      if (!reason && Math.abs(window.scrollY - y0) > SCROLL_TOLERANCE) reason = 'scrolled';
      if (!reason && window.location.href !== href0) reason = 'url changed';
      if (reason) {
        if (DEBUG) console.log('[si-page-analytics] not dead (' + reason + '):', info.element);
        return;
      }
      send('dead_click', info);
    }, 700);
  }

  // --- click listener ------------------------------------------------------
  document.addEventListener('click', function (ev) {
    try {
      var t = ev.target;
      if (!t || !t.closest) return;
      var sec = sectionOf(t);
      var info = { section: sec.name, zone: zoneOf(t), element: elementKey(t) };
      send('ui_click', Object.assign({}, info, position(ev, t)));
      checkRage(ev, info);
      checkDead(info, t);
    } catch (e) {
      if (DEBUG) console.warn('[si-page-analytics] click handler failed', e);
    }
  }, { capture: true, passive: true });
})();
